import { useTranslation } from "react-i18next";
import { Button, Center, Loader } from "@mantine/core";
import { GlobalContext } from "../context/GlobalContext";
import { useNavigate, useLocation } from "react-router-dom";
import { ContinueButton } from "../components/ContinueButton/ContinueButton";
import { Paths } from "../models/constants/paths";
import { defaultContext } from "../models/constants/defaultContext";
import { useContext, useEffect, useState } from "react";
import { IneligibilityReasons } from "../models/constants/ineligibilityReasons";
import { PageTitleMetaTags } from "../components/PageTitleMetaTags/PageTitleMetaTags";
import { AzureBlobFiles } from "../models/constants/azureBlobFiles";

export const IneligibleVehicle = () => {
  const { t } = useTranslation();
  const { context, setContext } = useContext(GlobalContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [header, setHeader] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [ineligibilityReasons, setIneligibilityReasons] = useState([]);
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [year, setYear] = useState(""); 
  const [mileage, setMileage] = useState("");
  const noVehicle =
    ineligibilityReasons[0] === IneligibilityReasons.NO_ELIGIBLE_VEHICLE &&
    ineligibilityReasons.length === 1;
  const pageDescription = t("browser-tab-descriptions.ineligible-vehicle");
  const metaDescription = t("browser-meta-descriptions.ineligible-vehicle");

  useEffect(() => {
    if (location?.state?.reasons) {
      setIneligibilityReasons(location.state.reasons);
      setMake(context.vehicle_data.make);
      setModel(context.vehicle_data.model);
      setYear(context.vehicle_data.year); 
      setMileage(context.vehicle_data.mileage); 
    } else {
      setContext(defaultContext);
      navigate(Paths.Home);
    }
    setContext(defaultContext);
  }, []);

  useEffect(() => {
    setIsLoading(false);
  }, [ineligibilityReasons]);

  const textReasons = {
    [IneligibilityReasons.INELIGIBLE_MAKE_MODEL]: {
      header: t(
        "ineligible-reasons.ineligible-vehicleclass.header",
        {
          make: make,
          model: model,
        }
      ), 
      title: t("ineligible-reasons.ineligible-vehicleclass.title")
    }, 
    [IneligibilityReasons.INELIGIBLE_NO_MAKE_MATCH]: {
      header: t("ineligible-reasons.ineligible-vehicleclass.header", { make: make, model: model }), 
      title: t("ineligible-reasons.ineligible-vehicleclass.title")
    }, 
    [IneligibilityReasons.INELIGIBLE_VEHICLE_AGE]: {
      header: t("ineligible-reasons.ineligible-year.header", {year: year, minimumYear: calculateMinimumEligibleYear(year)}),
      title: t("ineligible-reasons.ineligible-year.title")
    },
    [IneligibilityReasons.INELIGIBLE_MILEAGE]: {
      header: t("ineligible-reasons.ineligible-mileage.header", {mileage: mileage?.toLocaleString()}), 
      title: t("ineligible-reasons.ineligible-mileage.title")
    }, 
    [IneligibilityReasons.INELIGIBLE_LIFT_KIT_6_INCHES]: {
      header: t("ineligible-reasons.ineligible-liftkit.header"), 
      title: t("ineligible-reasons.ineligible-liftkit.title")
    }, 
    [IneligibilityReasons.INELIGIBLE_AFTERMARKET_PERFORMANCE]: {
      header: t("ineligible-reasons.ineligible-aftermarket.header"), 
      title: t("ineligible-reasons.ineligible-aftermarket.title")
    }, 
    [IneligibilityReasons.SHOP_NOT_PARTICIPATING]: {
      header: t("shop-not-participating.header"), 
      title: t("shop-not-participating.title")
    }, 
    [IneligibilityReasons.BAD_VIN_FORMAT]: {
      header: t("ineligible-reasons.ineligible-vin.header"), 
      title: t("ineligible-reasons.ineligible-vin.title")
    }, 
    [IneligibilityReasons.INELIGIBLE_VIN]: {
      header: t("ineligible-reasons.ineligible-vin.header"), 
      title: t("ineligible-reasons.ineligible-vin.title")
    }, 
    [IneligibilityReasons.INCORRECT_VEHICLE_INFORMATION]: {
      header: t("ineligible-reasons.ineligible-quote-error.header"), 
      title: t("ineligible-reasons.ineligible-quote-error.title")
    }
  }; 

  function calculateMinimumEligibleYear() {
    const currentYear = new Date().getFullYear(); 
    return (currentYear - 15).toString(); 
  }

  function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  function generateHeader() {
    if (noVehicle) {
      return t("ineligible-reasons.ineligible-no-qualifying-inspections.header");
    } else if (ineligibilityReasons.length > 1) {
      return t("ineligible-reasons.ineligible-multiple.header")
    } else {
      return textReasons[ineligibilityReasons[0]].header;
    }
  }

  function generatePageTitle(){
    if (noVehicle) {
      return t("ineligible-reasons.ineligible-no-qualifying-inspections.title");
    } else if(ineligibilityReasons.length > 1) {
      return t("ineligible-reasons.ineligible-multiple.title")
    } else {
      return textReasons[ineligibilityReasons[0]].title;
    }
  }

  function generateButton() {
    if (noVehicle) {
      return (
        <Button
          fullWidth
          className="button"
          color="#FF671D"
          onClick={() =>
            window.open(
              process.env.REACT_APP_SCHEDULER_URL,
              "_blank",
              "noreferrer"
            )
          }
        >
          {t("schedule-inspection")}
        </Button>
      );
    } else {
      return (
        <ContinueButton
          label={t("start-new-quote")}
          clickAction={() => navigate(Paths.Home)}
        />
      );
    }
  }

  function displayIneligibilityReasons() {
    if (ineligibilityReasons.length === 1) {
      if (ineligibilityReasons[0] === IneligibilityReasons.NO_ELIGIBLE_VEHICLE) {
        return (
          <div className="subheader_centered">
            {t("schedule-an-inspection-to-qualify")}
          </div>
        );
      }
      else {
        return (
          <div className="subheader_centered">
            {t("ineligible-reasons.ineligible-reason.header")}
          </div>
        );
      }
    } else {
      return (
        <>
          <div className="subheader_centered">
            {ineligibilityReasons.map((reason) => {
              return <li>{capitalizeFirstLetter(textReasons[reason].header)}</li>;
            })}
          </div>
        </>
      );
    }
  }

  return isLoading ? (
    <div className="full-container">
      <div className="content-container">
        <Center>
          <Loader color="#006B5B" size="xl" />
        </Center>
      </div>
    </div>
  ) : (
    <div className="full-container">
      <PageTitleMetaTags
        pageTitle={generatePageTitle()}
        pageDescription={pageDescription}
        metaTitle={generatePageTitle()}
        metaDescription={metaDescription}
        metaImage={AzureBlobFiles.MetaImage}
      />
      <div className="full-container_ineligible-vehicle">
        <div className="content-container">
          <div className="header">
            <div className="header_2">
              <div className="header_2_centered">{generateHeader()}</div>
            </div>
            <div className="subheader">{displayIneligibilityReasons()}</div>
            {generateButton()}
          </div>
        </div>
      </div>
    </div>
  );
};
